import { t } from '../../i18n/i18n';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import type { PathNode } from '../../engine/geometry/bezier';
import { type Vec, degToRad, dist, rotateAround } from '../../engine/geometry/vec';
import { lineShape, rectShape, ellipseShape, polygonShape } from '../../engine/model/design';
import { bboxUnion } from '../../engine/geometry/polygon';
import { invertTransform, objectBBox } from '../../engine/model/geometry';
import { defaultSatin } from '../../engine/model/params';
import type { Design, EmbObject } from '../../engine/model/types';
import { exportOrigin, stitchBBox } from '../../engine/stitch/stats';
import { updateObjects } from '../../state/designOps';
import { type AppState, store, useApp } from '../../state/store';
import { addShape, designBBox, select, selectionBBox, toggleSelect, zoomToBox, zoomToFit } from '../actions';
import { usePhone } from '../usePhone';
import { TouchGesture } from './touch';
import { wheelZoomFactor, zoomAt } from './zoom';
import { ZoomControls } from './ZoomControls';
import { FABRIC, buildStitchPaths, clearCanvas, drawGrid, drawStitchPaths, setViewTransform } from './render';
import { constrainAngle, measure, snapToNode } from './measure';
import { minNodes, nearestSegment, nodeInDesign, nodePaths, objectPath, setNodePath } from './shapes';

type Drag =
  | { kind: 'pan'; start: Vec; pan: Vec }
  | { kind: 'move'; start: Vec; base: Design; ids: string[]; key: string; moved: boolean }
  | { kind: 'scale'; corner: string; anchor: Vec; startBox: { w: number; h: number }; base: Design; ids: string[]; key: string; uniformOnly: boolean }
  | { kind: 'rotate'; center: Vec; startAngle: number; base: Design; ids: string[]; key: string }
  | { kind: 'node'; objectId: string; path: string; index: number; handle: 'p' | 'in' | 'out'; base: Design; key: string }
  | { kind: 'marquee'; start: Vec; cur: Vec; additive: boolean }
  | { kind: 'create'; tool: 'rect' | 'ellipse'; start: Vec; cur: Vec };

const HANDLE = 8;

function useSize(ref: React.RefObject<HTMLElement | null>): { w: number; h: number } {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

const selectView = (s: AppState) => ({
  viewport: s.viewport,
  layers: s.layers,
  tool: s.tool,
  selection: s.selection,
  selectedNode: s.selectedNode,
  result: s.stitch.result,
  image: s.image,
  highlight: s.highlight,
  pick: s.pick,
});

function shallowEq(a: ReturnType<typeof selectView>, b: ReturnType<typeof selectView>): boolean {
  return (Object.keys(a) as (keyof typeof a)[]).every((k) => a[k] === b[k]);
}

function useViewState() {
  const ref = useRef<ReturnType<typeof selectView> | null>(null);
  return useApp((s) => {
    const next = selectView(s);
    if (ref.current && shallowEq(ref.current, next)) return ref.current;
    ref.current = next;
    return next;
  });
}

function measureText(a: Vec, b: Vec): string {
  const m = measure(a, b);
  return t('Length {len} mm ({inch} in) · Δx {dx} mm · Δy {dy} mm · angle {angle}°', {
    len: m.length.toFixed(1),
    inch: (m.length / 25.4).toFixed(2),
    dx: Math.abs(m.dx).toFixed(1),
    dy: Math.abs(m.dy).toFixed(1),
    angle: m.angle.toFixed(1),
  });
}

export function CanvasView() {
  const design = useApp((s) => s.history.present);
  const { viewport: v, layers, tool, selection, selectedNode, result, image, highlight, pick } = useViewState();
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { w, h } = useSize(stageRef);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [draft, setDraft] = useState<Vec[]>([]);
  const [hover, setHover] = useState<Vec | null>(null);
  // measuring tool: a → b; `done` once the second point is placed
  const [ruler, setRuler] = useState<{ a: Vec; b: Vec; done: boolean } | null>(null);
  const [snapMark, setSnapMark] = useState<Vec | null>(null);
  const rulerDown = useRef(false);
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
  const fitted = useRef(false);
  const [touch] = useState(() => new TouchGesture());
  const handleScale = usePhone() ? 1.8 : 1;

  // fit on first layout and whenever requested (e.g. after import or loading a project)
  const fitNonce = useApp((s) => s.fitNonce);
  useEffect(() => {
    if (w > 0) {
      fitted.current = true;
      zoomToFit(w, h);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitNonce, w > 0]);

  useEffect(() => {
    if (!image) {
      setImgEl(null);
      return;
    }
    const el = new Image();
    el.onload = () => setImgEl(el);
    el.src = image.url;
  }, [image]);

  // the sequence already excludes hidden objects, so canvas and export show the same stitches
  const paths = useMemo(() => (result && typeof Path2D !== 'undefined' ? buildStitchPaths(result.sequence) : null), [result]);

  // ---- canvas drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !w) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const raf = requestAnimationFrame(() => {
      clearCanvas(ctx, canvas.width, canvas.height, FABRIC);
      if (layers.grid) drawGrid(ctx, v, dpr, w, h);
      if (layers.image && imgEl && design.backdrop) {
        setViewTransform(ctx, v, dpr);
        ctx.globalAlpha = design.backdrop.opacity;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(imgEl, design.backdrop.x, design.backdrop.y, design.backdrop.widthMm, design.backdrop.heightMm);
        ctx.globalAlpha = 1;
      }
      if (layers.stitches && paths) drawStitchPaths(ctx, paths, v, dpr, { showJumps: layers.jumps, opacity: layers.shapes ? 0.95 : 1 });
    });
    return () => cancelAnimationFrame(raf);
  }, [w, h, v, layers, imgEl, design.backdrop, paths]);

  // ---- coordinate helpers
  const toWorld = useCallback(
    (e: { clientX: number; clientY: number }): Vec => {
      const r = stageRef.current!.getBoundingClientRect();
      return { x: (e.clientX - r.left - v.panX) / v.zoom, y: (e.clientY - r.top - v.panY) / v.zoom };
    },
    [v],
  );
  const toScreen = (p: Vec): Vec => ({ x: p.x * v.zoom + v.panX, y: p.y * v.zoom + v.panY });

  const selected = useMemo(() => design.objects.filter((o) => selection.includes(o.id)), [design.objects, selection]);
  const selBox = useMemo(() => (selected.length ? selectionBBox(selected) : null), [selected]);
  const single = selected.length === 1 ? selected[0] : null;

  // ---- hoop outline
  const hoop = useMemo(() => {
    if (!layers.hoop) return null;
    const m = design.machine;
    // without stitches the hoop is centred on the artwork, so it is visible as soon as it is chosen
    const c = m.hoopCenter ?? (result && result.sequence.xs.length ? exportOrigin(result.sequence, m) : { x: design.widthMm / 2, y: design.heightMm / 2 });
    return { x: c.x - m.hoop.widthMm / 2, y: c.y - m.hoop.heightMm / 2, w: m.hoop.widthMm, h: m.hoop.heightMm, c };
  }, [layers.hoop, design.machine, design.widthMm, design.heightMm, result]);

  const stitchBox = useMemo(() => (result ? stitchBBox(result.sequence) : null), [result]);

  // after choosing a machine or hoop: show the whole hoop together with the design
  const hoopFitNonce = useApp((s) => s.hoopFitNonce);
  const hoopFitted = useRef(0);
  useEffect(() => {
    if (hoopFitNonce === hoopFitted.current || !hoop || !(w > 0)) return;
    hoopFitted.current = hoopFitNonce;
    zoomToBox(bboxUnion(designBBox(design), { minX: hoop.x, minY: hoop.y, maxX: hoop.x + hoop.w, maxY: hoop.y + hoop.h }), w, h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoopFitNonce, hoop]);

  // ---- finishing click-drawn shapes
  const finishDraft = useCallback(
    (points: Vec[]) => {
      setDraft([]);
      if (tool === 'polygon' && points.length >= 3) addShape(polygonShape(points), 'fill');
      else if (tool === 'line' && points.length >= 2) addShape(lineShape(points), 'running');
      else if (tool === 'column' && points.length >= 2) addShape(lineShape(points), { ...defaultSatin(), lineWidth: 3 });
    },
    [tool],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!draft.length) return;
      if (e.key === 'Enter') finishDraft(draft);
      if (e.key === 'Escape') setDraft([]);
      if (e.key === 'Backspace') setDraft((d) => d.slice(0, -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draft, finishDraft]);

  useEffect(() => {
    setDraft([]);
    setRuler(null);
    setSnapMark(null);
  }, [tool]);

  // Esc clears the measurement first (capture phase, before the global shortcut leaves the tool)
  useEffect(() => {
    if (!ruler) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setRuler(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [ruler]);

  /** Measuring point: snaps to object nodes within 8 px; Shift locks the direction to 15° steps. */
  const measurePoint = (e: { shiftKey: boolean }, p: Vec, anchor: Vec | null): { p: Vec; snapped: boolean } => {
    const snapped = snapToNode(p, design.objects, 8 / v.zoom);
    if (snapped) return { p: snapped, snapped: true };
    return { p: anchor && e.shiftKey ? constrainAngle(anchor, p) : p, snapped: false };
  };

  // ---- pointer handling
  const stageOrigin = (): Vec => {
    const r = stageRef.current!.getBoundingClientRect();
    return { x: r.left, y: r.top };
  };
  /** A second finger turns the gesture into pinch/pan: undo what the first finger started. */
  const cancelForPinch = () => {
    if (drag && 'base' in drag && store.getState().history.present !== drag.base) {
      store.undo();
      store.set((s) => ({ history: { ...s.history, future: [] } }));
    }
    rulerDown.current = false;
    setDrag(null);
  };
  const onPointerDown = (e: RPointerEvent<SVGSVGElement>) => {
    const target = e.target as Element;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    if (touch.down(e, stageOrigin(), v)) {
      cancelForPinch();
      return;
    }
    const p = toWorld(e);
    if (e.button === 1 || e.button === 2 || tool === 'pan' || spaceDown.current || (e.button === 0 && e.altKey && tool === 'select')) {
      setDrag({ kind: 'pan', start: { x: e.clientX, y: e.clientY }, pan: { x: v.panX, y: v.panY } });
      return;
    }
    if (e.button !== 0) return;

    if (tool === 'measure') {
      const q = measurePoint(e, p, ruler && !ruler.done ? ruler.a : null).p;
      rulerDown.current = true;
      if (!ruler || ruler.done) setRuler({ a: q, b: q, done: false });
      else setRuler({ ...ruler, b: q, done: true });
      return;
    }

    if (pick) {
      const st = store.getState();
      if (pick === 'hoopCenter') {
        store.commitDesign({ ...design, machine: { ...design.machine, hoopCenter: p } });
      } else if (st.selection.length === 1) {
        store.commitDesign(updateObjects(design, st.selection, (o) => ({ ...o, [pick]: p })));
      }
      store.set({ pick: null });
      return;
    }

    const handle = target.getAttribute('data-handle');
    const id = target.closest('[data-id]')?.getAttribute('data-id') ?? null;
    const key = `drag:${Date.now()}`;

    if (tool === 'rect' || tool === 'ellipse') {
      setDrag({ kind: 'create', tool, start: p, cur: p });
      return;
    }
    if (tool === 'polygon' || tool === 'line' || tool === 'column') {
      if (e.detail >= 2) return; // dblclick handled separately
      const last = draft[draft.length - 1];
      if (draft.length >= 3 && tool === 'polygon' && dist(p, draft[0]) * v.zoom < 10) {
        finishDraft(draft);
        return;
      }
      if (!last || dist(last, p) > 0.05) setDraft([...draft, p]);
      return;
    }

    if (tool === 'node') {
      const nodeAttr = target.getAttribute('data-node');
      if (nodeAttr && single) {
        const [path, idx, which] = nodeAttr.split('|');
        store.set({ selectedNode: { objectId: single.id, path, index: Number(idx) } });
        if (!single.locked) setDrag({ kind: 'node', objectId: single.id, path, index: Number(idx), handle: which as 'p' | 'in' | 'out', base: design, key });
        return;
      }
      if (id) select([id]);
      else select([]);
      return;
    }

    // select tool
    if (handle && selBox && selected.length) {
      const ids = selected.filter((o) => !o.locked).map((o) => o.id);
      if (!ids.length) return;
      if (handle === 'rotate') {
        const c = { x: (selBox.minX + selBox.maxX) / 2, y: (selBox.minY + selBox.maxY) / 2 };
        setDrag({ kind: 'rotate', center: c, startAngle: Math.atan2(p.y - c.y, p.x - c.x), base: design, ids, key });
      } else {
        const anchor = {
          x: handle.includes('w') ? selBox.maxX : selBox.minX,
          y: handle.includes('n') ? selBox.maxY : selBox.minY,
        };
        const uniformOnly = selected.some((o) => Math.abs(o.transform.rotation % 360) > 1e-6);
        setDrag({ kind: 'scale', corner: handle, anchor, startBox: { w: selBox.maxX - selBox.minX, h: selBox.maxY - selBox.minY }, base: design, ids, key, uniformOnly });
      }
      return;
    }
    if (id) {
      const obj = design.objects.find((o) => o.id === id);
      if (!selection.includes(id) || e.shiftKey) toggleSelect(id, e.shiftKey);
      const ids = (e.shiftKey ? store.getState().selection : selection.includes(id) ? selection : [id]).filter(
        (x) => !design.objects.find((o) => o.id === x)?.locked,
      );
      if (obj && !obj.locked && ids.length) setDrag({ kind: 'move', start: p, base: design, ids, key, moved: false });
      return;
    }
    setDrag({ kind: 'marquee', start: p, cur: p, additive: e.shiftKey });
    if (!e.shiftKey) select([]);
  };

  const onPointerMove = (e: RPointerEvent<SVGSVGElement>) => {
    const pinched = touch.move(e, stageOrigin());
    if (pinched) store.set({ viewport: pinched });
    if (touch.active) return;
    const p = toWorld(e);
    setHover(p);
    if (tool === 'measure') {
      const q = measurePoint(e, p, ruler && !ruler.done ? ruler.a : null);
      setSnapMark(q.snapped ? q.p : null);
      if (ruler && !ruler.done) setRuler({ ...ruler, b: q.p });
    }
    if (!drag) return;
    switch (drag.kind) {
      case 'pan':
        store.set({ viewport: { ...v, panX: drag.pan.x + e.clientX - drag.start.x, panY: drag.pan.y + e.clientY - drag.start.y } });
        break;
      case 'move': {
        let dx = p.x - drag.start.x, dy = p.y - drag.start.y;
        if (e.shiftKey) {
          if (Math.abs(dx) > Math.abs(dy)) dy = 0;
          else dx = 0;
        }
        if (!drag.moved && Math.hypot(dx, dy) * v.zoom < 3) return;
        drag.moved = true;
        store.commitDesign(
          updateObjects(drag.base, drag.ids, (o) => ({
            ...o,
            transform: { ...o.transform, x: o.transform.x + dx, y: o.transform.y + dy },
            entry: o.entry ? { x: o.entry.x + dx, y: o.entry.y + dy } : null,
            exit: o.exit ? { x: o.exit.x + dx, y: o.exit.y + dy } : null,
          })),
          drag.key,
        );
        break;
      }
      case 'scale': {
        const a = drag.anchor;
        let sx = drag.startBox.w > 1e-6 ? Math.abs(p.x - a.x) / drag.startBox.w : 1;
        let sy = drag.startBox.h > 1e-6 ? Math.abs(p.y - a.y) / drag.startBox.h : 1;
        if (e.shiftKey || drag.uniformOnly) sx = sy = Math.max(sx, sy);
        sx = Math.max(0.01, sx);
        sy = Math.max(0.01, sy);
        const sp = (q: Vec): Vec => ({ x: a.x + (q.x - a.x) * sx, y: a.y + (q.y - a.y) * sy });
        store.commitDesign(
          updateObjects(drag.base, drag.ids, (o) => {
            const t = sp({ x: o.transform.x, y: o.transform.y });
            return {
              ...o,
              transform: { ...o.transform, x: t.x, y: t.y, scaleX: o.transform.scaleX * sx, scaleY: o.transform.scaleY * sy },
              entry: o.entry ? sp(o.entry) : null,
              exit: o.exit ? sp(o.exit) : null,
            };
          }),
          drag.key,
        );
        break;
      }
      case 'rotate': {
        let a = Math.atan2(p.y - drag.center.y, p.x - drag.center.x) - drag.startAngle;
        if (e.shiftKey) a = Math.round(a / degToRad(15)) * degToRad(15);
        const deg = (a * 180) / Math.PI;
        const rot = (q: Vec) => rotateAround(q, drag.center, a);
        store.commitDesign(
          updateObjects(drag.base, drag.ids, (o) => {
            const t = rot({ x: o.transform.x, y: o.transform.y });
            return { ...o, transform: { ...o.transform, x: t.x, y: t.y, rotation: normalizeDeg(o.transform.rotation + deg) }, entry: o.entry ? rot(o.entry) : null, exit: o.exit ? rot(o.exit) : null };
          }),
          drag.key,
        );
        break;
      }
      case 'node': {
        const obj = drag.base.objects.find((o) => o.id === drag.objectId);
        if (!obj) return;
        const local = invertTransform(p, obj.transform);
        const ref = nodePaths(obj.shape).find((r) => r.key === drag.path);
        if (!ref) return;
        const nodes = ref.nodes.map((n, i): PathNode => {
          if (i !== drag.index) return n;
          if (drag.handle === 'in') return { ...n, in: local };
          if (drag.handle === 'out') return { ...n, out: local };
          const dx = local.x - n.x, dy = local.y - n.y;
          return {
            x: local.x,
            y: local.y,
            ...(n.in ? { in: { x: n.in.x + dx, y: n.in.y + dy } } : {}),
            ...(n.out ? { out: { x: n.out.x + dx, y: n.out.y + dy } } : {}),
          };
        });
        store.commitDesign(updateObjects(drag.base, [obj.id], (o) => ({ ...o, shape: setNodePath(o.shape, drag.path, nodes) })), drag.key);
        break;
      }
      case 'marquee':
      case 'create':
        setDrag({ ...drag, cur: p });
        break;
    }
  };

  const onPointerUp = (e: RPointerEvent<SVGSVGElement>) => {
    if (touch.up(e)) return;
    if (tool === 'measure' && rulerDown.current) {
      rulerDown.current = false;
      // click-drag-release measures in one go
      if (ruler && !ruler.done && dist(ruler.a, ruler.b) * v.zoom > 6) setRuler({ ...ruler, done: true });
    }
    if (!drag) return;
    if (drag.kind === 'marquee') {
      const b = { minX: Math.min(drag.start.x, drag.cur.x), maxX: Math.max(drag.start.x, drag.cur.x), minY: Math.min(drag.start.y, drag.cur.y), maxY: Math.max(drag.start.y, drag.cur.y) };
      if ((b.maxX - b.minX) * v.zoom > 3) {
        const ids = design.objects
          .filter((o) => o.visible && !o.locked)
          .filter((o) => {
            const ob = objectBBox(o);
            return ob.minX <= b.maxX && ob.maxX >= b.minX && ob.minY <= b.maxY && ob.maxY >= b.minY;
          })
          .map((o) => o.id);
        select(drag.additive ? [...new Set([...store.getState().selection, ...ids])] : ids);
      }
    } else if (drag.kind === 'create') {
      const x = Math.min(drag.start.x, drag.cur.x), y = Math.min(drag.start.y, drag.cur.y);
      const ww = Math.abs(drag.cur.x - drag.start.x), hh = Math.abs(drag.cur.y - drag.start.y);
      if (ww > 0.5 && hh > 0.5) {
        addShape(drag.tool === 'rect' ? rectShape(x, y, ww, hh) : ellipseShape(x + ww / 2, y + hh / 2, ww / 2, hh / 2), 'fill');
        store.set({ tool: 'select' });
      }
    }
    setDrag(null);
  };

  const onDoubleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const p = toWorld(e);
    if (tool === 'polygon' || tool === 'line' || tool === 'column') {
      finishDraft(draft);
      return;
    }
    if (tool === 'node' && single && !single.locked) {
      // insert a node on the nearest segment
      const local = invertTransform(p, single.transform);
      const refs = nodePaths(single.shape);
      let bestRef = refs[0], bestSeg = 0, bd = Infinity;
      for (const r of refs) {
        const seg = nearestSegment(r.nodes, r.closed, local);
        const a = r.nodes[seg], b = r.nodes[(seg + 1) % r.nodes.length];
        const d = Math.hypot((a.x + b.x) / 2 - local.x, (a.y + b.y) / 2 - local.y);
        if (d < bd) {
          bd = d;
          bestRef = r;
          bestSeg = seg;
        }
      }
      const nodes = bestRef.nodes.slice();
      nodes.splice(bestSeg + 1, 0, { x: local.x, y: local.y });
      store.commitDesign(updateObjects(design, [single.id], (o) => ({ ...o, shape: setNodePath(o.shape, bestRef.key, nodes) })));
      store.set({ selectedNode: { objectId: single.id, path: bestRef.key, index: bestSeg + 1 } });
    } else if (tool === 'select' && single) {
      store.set({ tool: 'node' });
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    const r = stageRef.current!.getBoundingClientRect();
    // functional updates: several wheel events can arrive before the next render
    if (e.shiftKey) {
      store.set((s) => ({ viewport: { ...s.viewport, panX: s.viewport.panX - e.deltaY, panY: s.viewport.panY - e.deltaX } }));
      return;
    }
    const factor = wheelZoomFactor(e);
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    store.set((s) => ({ viewport: zoomAt(s.viewport, factor, mx, my) }));
  };

  // hold space (or use the middle/right mouse button) to pan with any tool
  const spaceDown = useRef(false);
  useEffect(() => {
    const isText = (t: EventTarget | null) => t instanceof Element && !!t.closest('input, textarea, select, [contenteditable="true"]');
    // Space pans (also when a toolbar button has focus — it must not "press" that button)
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || isText(e.target) || store.getState().panel) return;
      e.preventDefault();
      spaceDown.current = true;
    };
    const up = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !spaceDown.current) return;
      e.preventDefault();
      spaceDown.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // non-passive wheel listener to prevent page zoom/scroll
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const prevent = (e: WheelEvent) => e.preventDefault();
    el.addEventListener('wheel', prevent, { passive: false });
    return () => el.removeEventListener('wheel', prevent);
  }, []);

  const cursor =
    pick ? 'crosshair' : drag?.kind === 'pan' || tool === 'pan' ? 'grab' : tool === 'select' || tool === 'node' ? 'default' : 'crosshair';

  const colorOf = useMemo(() => new Map(design.palette.map((c) => [c.id, c.hex])), [design.palette]);
  const shapeOpacity = layers.stitches && result ? 0.18 : 0.75;

  return (
    <div className="stage" ref={stageRef} onWheel={onWheel} data-testid="stage">
      <canvas ref={canvasRef} aria-hidden="true" />
      <svg
        role="application"
        aria-label={t('Design canvas')}
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={(e) => {
          if (touch.up(e) || e.pointerType === 'touch') setDrag(null);
        }}
        onPointerLeave={() => setHover(null)}
        onDoubleClick={onDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        <g transform={`translate(${v.panX} ${v.panY}) scale(${v.zoom})`}>
          {hoop && (
            <g pointerEvents="none">
              <rect x={hoop.x} y={hoop.y} width={hoop.w} height={hoop.h} rx={4} fill="none" stroke="#5b6270" strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
              <path d={`M${hoop.c.x - 3} ${hoop.c.y}H${hoop.c.x + 3}M${hoop.c.x} ${hoop.c.y - 3}V${hoop.c.y + 3}`} stroke="#5b6270" vectorEffect="non-scaling-stroke" />
            </g>
          )}
          {layers.shapes &&
            design.objects.map((o) => {
              if (!o.visible) return null;
              const path = objectPath(o);
              const color = colorOf.get(o.colorId) ?? '#888';
              const isSel = selection.includes(o.id);
              const isHi = highlight === o.id;
              return (
                <g key={o.id} data-id={o.id} style={{ cursor: o.locked ? 'not-allowed' : tool === 'select' ? 'move' : undefined }}>
                  {path.outline && <path d={path.outline} fill={color} fillOpacity={shapeOpacity} stroke="none" />}
                  <path
                    d={path.d}
                    fill={path.closed ? color : 'none'}
                    fillOpacity={path.closed ? shapeOpacity : 0}
                    fillRule="evenodd"
                    stroke={isSel || isHi ? '#ff7a59' : color}
                    strokeOpacity={isSel || isHi ? 1 : 0.9}
                    strokeWidth={isSel || isHi ? 2 : path.closed ? 1 : 2}
                    vectorEffect="non-scaling-stroke"
                  />
                  {/* wide invisible hit area for thin shapes */}
                  {!path.closed && <path d={path.d} fill="none" stroke="transparent" strokeWidth={12} vectorEffect="non-scaling-stroke" />}
                </g>
              );
            })}
          {selected.map((o) => (
            <g key={'pts' + o.id} pointerEvents="none">
              {o.entry && <circle cx={o.entry.x} cy={o.entry.y} r={5 / v.zoom} fill="#2bb673" stroke="#fff" strokeWidth={1.5 / v.zoom} />}
              {o.exit && <circle cx={o.exit.x} cy={o.exit.y} r={5 / v.zoom} fill="#d62828" stroke="#fff" strokeWidth={1.5 / v.zoom} />}
            </g>
          ))}
          {draft.length > 0 && (
            <polyline
              points={[...draft, ...(hover ? [hover] : [])].map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="#ff7a59"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          )}
          {drag?.kind === 'create' && (
            drag.tool === 'rect' ? (
              <rect x={Math.min(drag.start.x, drag.cur.x)} y={Math.min(drag.start.y, drag.cur.y)} width={Math.abs(drag.cur.x - drag.start.x)} height={Math.abs(drag.cur.y - drag.start.y)} fill="#ff7a5922" stroke="#ff7a59" vectorEffect="non-scaling-stroke" />
            ) : (
              <ellipse cx={(drag.start.x + drag.cur.x) / 2} cy={(drag.start.y + drag.cur.y) / 2} rx={Math.abs(drag.cur.x - drag.start.x) / 2} ry={Math.abs(drag.cur.y - drag.start.y) / 2} fill="#ff7a5922" stroke="#ff7a59" vectorEffect="non-scaling-stroke" />
            )
          )}
          {ruler && (
            <g pointerEvents="none" data-testid="ruler">
              <line x1={ruler.a.x} y1={ruler.a.y} x2={ruler.b.x} y2={ruler.b.y} stroke="#fff" strokeWidth={4} strokeOpacity={0.7} vectorEffect="non-scaling-stroke" />
              <line x1={ruler.a.x} y1={ruler.a.y} x2={ruler.b.x} y2={ruler.b.y} stroke="#ff7a59" strokeWidth={2} vectorEffect="non-scaling-stroke" />
              {[ruler.a, ruler.b].map((q, i) => (
                <circle key={i} cx={q.x} cy={q.y} r={4 / v.zoom} fill="#ff7a59" stroke="#fff" strokeWidth={1.5 / v.zoom} />
              ))}
            </g>
          )}
          {tool === 'measure' && snapMark && (
            <rect x={snapMark.x - 6 / v.zoom} y={snapMark.y - 6 / v.zoom} width={12 / v.zoom} height={12 / v.zoom} fill="none" stroke="#7cb7ff" strokeWidth={2} vectorEffect="non-scaling-stroke" pointerEvents="none" />
          )}
          {drag?.kind === 'marquee' && (
            <rect x={Math.min(drag.start.x, drag.cur.x)} y={Math.min(drag.start.y, drag.cur.y)} width={Math.abs(drag.cur.x - drag.start.x)} height={Math.abs(drag.cur.y - drag.start.y)} fill="#7cb7ff18" stroke="#7cb7ff" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
          )}
        </g>

        {/* screen-space handles */}
        {tool === 'select' && selBox && selected.some((o) => !o.locked) && !drag?.kind.startsWith('m') && (
          <SelectionHandles box={selBox} toScreen={toScreen} k={handleScale} />
        )}
        {tool === 'node' && single && <NodeHandles obj={single} toScreen={toScreen} selected={selectedNode} k={handleScale} />}
      </svg>

      <div className="overlay-br" aria-live="polite">
        {hover && <span className="chip">{hover.x.toFixed(1)}, {hover.y.toFixed(1)} mm</span>}
        {stitchBox && stitchBox.maxX >= stitchBox.minX && (
          <span className="chip">{(stitchBox.maxX - stitchBox.minX).toFixed(1)} × {(stitchBox.maxY - stitchBox.minY).toFixed(1)} mm</span>
        )}
        <ZoomControls stage={() => stageRef.current} onFit={() => zoomToFit(w, h)} />
      </div>
      {pick && (
        <div className="overlay-tl">
          <span className="chip">
            {pick === 'entry' ? t('Click on the canvas to choose the start point · Esc cancels') : pick === 'exit' ? t('Click on the canvas to choose the end point · Esc cancels') : t('Click on the canvas to choose the hoop centre · Esc cancels')}
          </span>
        </div>
      )}
      {ruler && dist(ruler.a, ruler.b) > 0 && (
        <span
          className="chip measure-label"
          style={{ left: toScreen(ruler.a).x / 2 + toScreen(ruler.b).x / 2, top: toScreen(ruler.a).y / 2 + toScreen(ruler.b).y / 2 }}
          data-testid="ruler-label"
        >
          {measure(ruler.a, ruler.b).length.toFixed(1)} mm
        </span>
      )}
      {tool === 'measure' && (
        <div className="overlay-tl">
          <span className="chip" aria-live="polite" data-testid="measure-info">
            {ruler && dist(ruler.a, ruler.b) > 0
              ? measureText(ruler.a, ruler.b)
              : t('Click two points to measure · Shift = 15° steps · snaps to nodes · Esc clears')}
          </span>
        </div>
      )}
      {draft.length > 0 && (
        <div className="overlay-tl">
          <span className="chip">{t('{n} points · Enter/double-click finishes · Backspace removes a point · Esc cancels', { n: draft.length })}</span>
        </div>
      )}
    </div>
  );
}

function normalizeDeg(d: number): number {
  let r = d % 360;
  if (r > 180) r -= 360;
  if (r <= -180) r += 360;
  return Math.round(r * 1000) / 1000;
}

/** `k` scales the handles (larger for fingers on phones). */
function SelectionHandles({ box, toScreen, k }: { box: { minX: number; minY: number; maxX: number; maxY: number }; toScreen: (p: Vec) => Vec; k: number }) {
  const a = toScreen({ x: box.minX, y: box.minY });
  const b = toScreen({ x: box.maxX, y: box.maxY });
  const corners: [string, number, number][] = [
    ['nw', a.x, a.y],
    ['ne', b.x, a.y],
    ['sw', a.x, b.y],
    ['se', b.x, b.y],
  ];
  const cx = (a.x + b.x) / 2;
  return (
    <g>
      <rect x={a.x} y={a.y} width={b.x - a.x} height={b.y - a.y} fill="none" stroke="#ff7a59" strokeDasharray="4 3" pointerEvents="none" />
      <line x1={cx} y1={a.y} x2={cx} y2={a.y - 24 * k} stroke="#ff7a59" pointerEvents="none" />
      <circle data-handle="rotate" cx={cx} cy={a.y - 28 * k} r={6 * k} fill="#1a1d23" stroke="#ff7a59" strokeWidth={2} style={{ cursor: 'grab' }}>
        <title>{t('Rotate (Shift = 15° steps)')}</title>
      </circle>
      {corners.map(([corner, x, y]) => (
        <rect
          key={corner}
          data-handle={corner}
          x={x - (HANDLE * k) / 2}
          y={y - (HANDLE * k) / 2}
          width={HANDLE * k}
          height={HANDLE * k}
          fill="#fff"
          stroke="#ff7a59"
          strokeWidth={1.5}
          style={{ cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize' }}
        >
          <title>{t('Scale (Shift = keep proportions)')}</title>
        </rect>
      ))}
    </g>
  );
}

function NodeHandles({ obj, toScreen, selected, k }: { obj: EmbObject; toScreen: (p: Vec) => Vec; selected: { objectId: string; path: string; index: number } | null; k: number }) {
  return (
    <g>
      {nodePaths(obj.shape).map((ref) =>
        ref.nodes.map((n, i) => {
          const d = nodeInDesign(obj, n);
          const p = toScreen(d.p);
          const isSel = selected?.objectId === obj.id && selected.path === ref.key && selected.index === i;
          return (
            <g key={ref.key + i}>
              {d.in && (
                <>
                  <line x1={p.x} y1={p.y} x2={toScreen(d.in).x} y2={toScreen(d.in).y} stroke="#7cb7ff" pointerEvents="none" />
                  <circle data-node={`${ref.key}|${i}|in`} cx={toScreen(d.in).x} cy={toScreen(d.in).y} r={4 * k} fill="#7cb7ff" />
                </>
              )}
              {d.out && (
                <>
                  <line x1={p.x} y1={p.y} x2={toScreen(d.out).x} y2={toScreen(d.out).y} stroke="#7cb7ff" pointerEvents="none" />
                  <circle data-node={`${ref.key}|${i}|out`} cx={toScreen(d.out).x} cy={toScreen(d.out).y} r={4 * k} fill="#7cb7ff" />
                </>
              )}
              <rect
                data-node={`${ref.key}|${i}|p`}
                x={p.x - 5 * k}
                y={p.y - 5 * k}
                width={10 * k}
                height={10 * k}
                fill={isSel ? '#ff7a59' : '#fff'}
                stroke={ref.key.startsWith('rail') ? (ref.key === 'railA' ? '#2bb673' : '#7cb7ff') : '#ff7a59'}
                strokeWidth={1.5}
                style={{ cursor: 'move' }}
              >
                <title>{t('Drag to move · Delete removes (min. {n} points) · double-click an edge to add', { n: minNodes(ref.closed) })}</title>
              </rect>
            </g>
          );
        }),
      )}
    </g>
  );
}
